import React, { useEffect, useState } from 'react';
import { MethodFormProps } from './MethodForm';
import FileDragAndDrop from '../../../../components/_atom/file/FileDragAndDrop';
import BasicButton from '../../../../components/_atom/button/BasicButton';
import { ButtonSize, ButtonTheme } from '../../../../components/_atom/button/StyledButton';
import iconFailTransaction from '../../../../assets/images/Icon_fail_transaction.png';
import DivideLine from '../../../../components/_atom/line/DivideLine';
import Switch from '../../../../components/_atom/switch/Switch';
import TagBadge, { TagBadgeType } from '../TagBadge';
import CommentInput from '../../../../components/_atom/input/CommentInput';
import toast from 'react-hot-toast';
import { logic } from '../../../../../logic';
import { ConnectedWallet, Kit, ResourceRegistry } from '../../../../assets/constant/kit.type';
import { errorAlert } from '../../../../../utils/Utils';
import EnterPinCodeModal from '../EnterPinCodeModal';
import { getBlockExplorerDetailAction } from '../../../../../action/blockExplorerAction';
import MethodResult from './MethodResult';
import retryIcon from '../../../../assets/images/icon_retry_grey.png';

const METADATA_EXTENSION = {
  'image/*': ['.png', '.jpeg', '.gif', '.jpg', '.svg', '.webp'],
};
type MintResult = {
  batch_issuance_job_id: number;
  nft_kit_id: number;
  csv_file_uri: string;
  total_issuance_count: number;
  metadata_upload_status: number; //token Uri를 뽑아냈는지 확인할 수 있는값
  executed_issuance_count: number;
  job_status: string; // 'pending', 'executed', 'completed', 'failed'
  issuance_batch_size: number;
  // batch_issuances 배열의 length로 몇개의 Tx hash가 나오는지 알 수 있음
  batch_issuances: {
    batch_issuance_id: number;
    batch_issuance_job_id: number;
    tx_calldata: string;
    tx_hash: string; //Tx_hash
    used_tx_nonce: number;
    issuance_start_index: number; //사용자가 보낸 몇번째 인덱스부터 시작하는지
    issuance_end_index: number; // 사용자가 보낸 몇번째 인덱스까지 실행하는지
    issuance_status: string; // 'pending', 'executed' = 성공표시, 'succeed', 'failed' = 실패표시
    failure_reason: string; //실패사유
  }[];
};
type BatchMethodFormProps = {
  resourceRegistry?: ResourceRegistry;
} & Omit<
  MethodFormProps,
  | 'setIsOpenEnterPinCodeModal'
  | 'password'
  | 'setPassword'
  | 'currentInput'
  | 'inputValue'
  | 'setInputValue'
  | 'multipleInputValues'
  | 'setMultipleInputValues'
  | 'result'
>;

const BatchMethodForm = (props: BatchMethodFormProps) => {
  const {
    method,
    hasTXWebhook,
    kitDetail,
    connectedWallet,
    setSelectedMethodType,
    useWebhook,
    setUseWebhook,
    webhookUrl,
    setWebhookUrl,
    loading,
    setLoading,
    runMethod,
    resourceRegistry,
  } = props;
  const { need_wallet, owner_only } = method;

  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const maxMediaFileCount = connectedWallet.connectWalletType === 'metamask' ? 30 : null;
  const [csvFile, setCsvFile] = useState<File>();

  const downloadCSVTemplate = async () => {
    const mediaFileNames = mediaFiles.map((file) => ({
      imageName: file.name,
    }));
    const { error } = await logic.nftKit.createCSVFile(mediaFileNames);
    if (error) {
      toast.error(error.data);
      return;
    }
  };
  const [isOpenEnterPinCodeModal, setIsOpenEnterPinCodeModal] = useState(false);
  const [password, setPassword] = useState('');

  const [mintResult, setMintResult] = useState<MintResult | string>();
  const mintWithMetamask = async () => {
    if (!kitDetail?.nft_kit_id || !resourceRegistry || !kitDetail.chain_id || !kitDetail.contract_address) {
      toast.error('Invalid kit detail or resource registry');
      return;
    }
    if (mediaFiles.length === 0) {
      toast.error('Please upload media files first');
      return;
    }
    if (!csvFile) {
      toast.error('Please upload csv file first');
      return;
    }
    const { error: uploadMediaError } = await logic.nftKit.createS3UrlFromImageFiles(
      mediaFiles,
      kitDetail.nft_kit_id,
      kitDetail.category_identifier,
      resourceRegistry.service_identifier
    );
    if (uploadMediaError) {
      toast.error(uploadMediaError.data);
      return;
    }
    const { result: uploadMetadataResult, error: uploadMetadataError } = await logic.nftKit.createS3UrlFromFiles(kitDetail.nft_kit_id, csvFile);
    if (uploadMetadataError) {
      toast.error(uploadMetadataError.data);
      return;
    }
    const parsedMetadata = uploadMetadataResult.map((item) => ({
      to: item.recipient,
      tokenUri: item.token_uri,
    }));
    const { result, error } = await logic.nftKit.nftKitIssuancesWithMetamask(parsedMetadata, kitDetail.chain_id, kitDetail.contract_address);
    if (error) {
      toast.error('Failed to mint NFTs. Please try again later.');
      return;
    }
    setMintResult(result);
  };

  const [pollingId, setPollingId] = useState<number>();
  const mintWithAccountManager = async () => {
    if (!kitDetail?.nft_kit_id || !resourceRegistry || !kitDetail.chain_id || !kitDetail.contract_address) {
      toast.error('Invalid kit detail or resource registry');
      return;
    }
    if (mediaFiles.length === 0) {
      toast.error('Please upload media files first');
      return;
    }
    if (!csvFile) {
      toast.error('Please upload csv file first');
      return;
    }
    const { error: uploadMediaError } = await logic.nftKit.createS3UrlFromImageFiles(
      mediaFiles,
      kitDetail.nft_kit_id,
      kitDetail.category_identifier,
      resourceRegistry.service_identifier
    );
    if (uploadMediaError) {
      toast.error('Failed to upload media files');
      return;
    }
    const { result, error } = await logic.nftKit.nftKitCSVFileIssuancesWithServer(kitDetail.nft_kit_id, password, csvFile, {
      useWebhook,
      webhookUrl,
    });
    if (error) {
      toast.error('Failed to mint NFTs. Please try again later.');
      return;
    }
    setMintResult(result);
    setPollingId(result.batch_issuance_job_id);
  };
  const batchMint = async () => {
    const { connectWalletType: walletType } = connectedWallet || {};
    if (walletType === 'metamask') {
      return mintWithMetamask();
    }
    if (walletType === 'accountManager') {
      return mintWithAccountManager();
    }
  };
  useEffect(() => {
    if (!pollingId || !kitDetail?.nft_kit_id) {
      return;
    }
    logic.nftKit.bulkMintHistoryPolling(pollingId, true, kitDetail.nft_kit_id, 3000, 50, setMintResult);
  }, [pollingId, kitDetail]);

  const isKitOwner = kitDetail?.admin_address?.toLowerCase() === connectedWallet.connectAddress?.toLowerCase();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <EnterPinCodeModal
        isOpen={isOpenEnterPinCodeModal}
        closeModal={async () => {
          setIsOpenEnterPinCodeModal(false);
          if (password.length === 6) {
            batchMint().finally(() => {
              setPassword('');
              setLoading(false);
            });
          }
        }}
        connectedWallet={connectedWallet}
        pin={password}
        setPin={setPassword}
        title={'Enter your Kit PIN'}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 30, marginBottom: 20 }}>
        <div className="kit-label-value-wrapper column">
          <div className="kit-label">1. Upload media files</div>
          <div className="kit-label-description">
            {"Upload media files for your NFTs.\nThe uploaded filenames will be linked to the CSV you'll download in the next step. "}
          </div>
          <div style={{ paddingLeft: 10 }}>
            {connectedWallet.connectWalletType === 'metamask' && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: 20,
                  marginBottom: 6,
                  borderRadius: 8,
                  backgroundColor: '#F9F9F9',
                  border: '1px solid #D8D8D8',
                  fontWeight: 500,
                  fontSize: 15,
                  lineHeight: '20px',
                  letterSpacing: 0,
                  whiteSpace: 'pre-line',
                }}
              >
                {'You can mint up to 30 tokens at once.\nThis limit will be removed if you connect Wallet using Account Manager.'}
              </div>
            )}
            <FileDragAndDrop
              type={'batch'}
              file={mediaFiles}
              setFile={setMediaFiles}
              maxFile={maxMediaFileCount}
              placeholder={'Image File Only / Max 10MB each'}
              accept={METADATA_EXTENSION}
              disabled={kitDetail?.status !== 4 || (owner_only && !isKitOwner)}
            />
          </div>
        </div>
        <div className="kit-label-value-wrapper column">
          <div className="kit-label">2. Download CSV Template</div>
          <div className="kit-label-description">Download the CSV template for NFT metadata and fill in the required information</div>
          <div style={{ paddingLeft: 10 }}>
            <BasicButton
              onClick={downloadCSVTemplate}
              size={ButtonSize.DYNAMIC}
              disabled={kitDetail?.status !== 4 || (owner_only && !isKitOwner) || mediaFiles.length === 0}
            >
              Download
            </BasicButton>
          </div>
        </div>
        <div className="kit-label-value-wrapper column">
          <div className="kit-label">3. Upload CSV</div>
          <div className="kit-label-description">Upload your completed CSV file with metadata</div>
          <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: 10, gap: 10 }}>
            <FileDragAndDrop
              file={csvFile}
              setFile={setCsvFile}
              maxFile={1}
              placeholder={'Upload CSV'}
              disabled={kitDetail?.status !== 4 || (owner_only && !isKitOwner)}
              accept={{ 'text/csv': ['.csv'] }}
            />
            <div style={{ padding: 20, borderRadius: 8, background: '#EFEFEF' }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
                <img src={iconFailTransaction} alt="icon_fail_transaction" style={{ width: 24, height: 24, objectFit: 'contain' }} />
                <div style={{ fontWeight: 700, fontSize: 16, lineHeight: '21px' }}>Note : Check your csv file</div>
              </div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: '20px',
                  whiteSpace: 'pre-line',
                }}
              >
                The <b>'name'</b> and <b>'recipient(to)'</b> columns are required fields.
              </div>
            </div>
          </div>
        </div>
      </div>
      {need_wallet && hasTXWebhook && (
        <>
          <DivideLine style={{ marginBottom: 20 }} />
          <div style={{ marginBottom: 20 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                fontWeight: 600,
                fontSize: 14,
                lineHeight: '20px',
              }}
            >
              Setting
            </div>
            <div>
              <div className="kit-label-value-wrapper" style={{ alignItems: 'center', marginTop: 20 }}>
                <div className="kit-label" style={{ minWidth: 'fit-content' }}>
                  Webhook (Transaction)
                </div>
                <Switch isOn={useWebhook} onToggle={() => setUseWebhook(!useWebhook)} />
                <TagBadge type={useWebhook ? TagBadgeType.BLUE : TagBadgeType.GREY} text={'Webhook Manager'} />
              </div>
              {useWebhook && (
                <div style={{ paddingLeft: 10, marginTop: 4 }}>
                  <CommentInput
                    value={webhookUrl}
                    disabled={kitDetail?.status !== 4 || (owner_only && !isKitOwner)}
                    placeholder={'Enter webhook url you want to receive event'}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {kitDetail?.status === 4 && (
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <BasicButton
            size={ButtonSize.DYNAMIC}
            style={{ width: 200, height: 40 }}
            theme={ButtonTheme.PRIMARY}
            onClick={async () => {
              setSelectedMethodType('single');
              if (need_wallet && connectedWallet.connectAddress === '') {
                toast.error('Please connect wallet first');
                return;
              }
              if (need_wallet && password.length !== 6 && connectedWallet.connectWalletType === 'accountManager') {
                setIsOpenEnterPinCodeModal(true);
                return;
              }
              batchMint().finally(() => {
                setPassword('');
                setLoading(false);
              });
            }}
            disabled={(owner_only && !isKitOwner) || loading}
          >
            Send
          </BasicButton>
        </div>
      )}
      {kitDetail?.status === 4 && mintResult && (
        <div style={{ marginTop: 30 }}>
          {typeof mintResult === 'string' ? (
            <MethodResult method={method} result={mintResult} kitDetail={kitDetail} />
          ) : (
            <MethodBatchResult
              mintResult={mintResult}
              kitDetail={kitDetail}
              useWebhook={useWebhook}
              webhookUrl={webhookUrl}
              setMintResult={setMintResult}
              connectedWallet={connectedWallet}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default BatchMethodForm;

enum IssuanceStatus {
  PENDING = 'pending',
  EXECUTED = 'executed',
  SUCCEED = 'succeed',
  FAILED = 'failed',
}

const MethodBatchResult = (props: {
  mintResult: MintResult;
  setMintResult: (mintResult: MintResult) => void;
  kitDetail: Kit;
  useWebhook: boolean;
  webhookUrl: string;
  connectedWallet: ConnectedWallet;
}) => {
  const { mintResult, kitDetail, useWebhook, webhookUrl, setMintResult, connectedWallet } = props;
  const { batch_issuances } = mintResult;

  const parseStatus = (issuance_status: string) => {
    switch (issuance_status) {
      case 'pending':
        return {
          status: IssuanceStatus.PENDING,
          text: 'Waiting',
        };
      case 'executed':
        return {
          status: IssuanceStatus.EXECUTED,
          text: 'Sent (Transaction Submitted)',
        };
      case 'succeed':
        return {
          status: IssuanceStatus.SUCCEED,
          text: 'Succeed (Transaction Completed)',
        };
      case 'failed':
        return {
          status: IssuanceStatus.FAILED,
          text: 'Failed',
        };
      default:
        return {
          status: IssuanceStatus.PENDING,
          text: 'Processing',
        };
    }
  };

  const [retryingIssuance, setRetryingIssuance] = useState<any>();
  const [isOpenEnterPinCodeModal, setIsOpenEnterPinCodeModal] = useState(false);
  const [password, setPassword] = useState('');

  const retryMint = async (issuance, password, useWebhook, webhookUrl) => {
    if (!kitDetail?.nft_kit_id) {
      toast.error('Invalid kit detail');
      return;
    }
    if (password.length !== 6) {
      setRetryingIssuance(issuance);
      setIsOpenEnterPinCodeModal(true);
      return;
    }
    const { batch_issuance_id } = issuance;
    const { result, error } = await logic.nftKit.bulkMintHistoryRetry(batch_issuance_id, kitDetail.nft_kit_id, password, {
      useWebhook,
      webhookUrl,
    });
    if (error) {
      errorAlert(error.data);
      return;
    }
    const { batch_issuances } = mintResult;
    const updatedBatchIssuances = batch_issuances.map((item) => {
      if (item.batch_issuance_id === batch_issuance_id) {
        return { ...item, tx_hash: result, issuance_status: 'executed' };
      }
      return item;
    });
    setMintResult({ ...mintResult, batch_issuances: updatedBatchIssuances });
    setRetryingIssuance(undefined);
  };

  const [blockExplorerURL, setBlockExplorerURL] = useState<string>('');
  const getMicroChainExplorerURL = async () => {
    const { result, error } = await getBlockExplorerDetailAction(kitDetail.chain_id);
    const { mainStatus, isExternal, microChainId: chainId } = result;
    if (result) {
      if (isExternal && result.externalTransactionUrl) {
        setBlockExplorerURL(result.externalTransactionUrl);
        return;
      }
      if (!isExternal && mainStatus === 5) {
        setBlockExplorerURL(`${process.env.REACT_APP_EQ_FIELD_URL}/${chainId}/transaction/list`);
        return;
      }
    }
    if (error) {
      //TODO: error
    }
  };
  useEffect(() => {
    if (kitDetail?.chain_id) {
      getMicroChainExplorerURL();
    }
  }, [kitDetail]);
  return (
    <div className="method-result-wrapper">
      <EnterPinCodeModal
        isOpen={isOpenEnterPinCodeModal}
        closeModal={async () => {
          setIsOpenEnterPinCodeModal(false);
          if (password.length === 6) {
            retryMint(retryingIssuance, password, useWebhook, webhookUrl).finally(() => {
              setPassword('');
            });
          }
        }}
        connectedWallet={connectedWallet}
        pin={password}
        setPin={setPassword}
        title={'Enter your Kit PIN'}
      />
      {batch_issuances.map((issuance, index) => {
        const status = parseStatus(issuance.issuance_status);
        return (
          <div key={'batch_issuance' + index}>
            <div className="method-result-label">Tx #{index + 1}</div>
            <div className="method-result-status-wrapper">
              <div className={`method-result-status ${status.status}`} />
              <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                <div className={`method-result-status-text ${status.status}`}>{status.text}</div>
                {issuance.failure_reason && <div className={`method-result-status-text ${status.status}`}>{issuance.failure_reason}</div>}
              </div>
              {status.status === IssuanceStatus.FAILED && (
                <BasicButton
                  size={ButtonSize.DYNAMIC}
                  theme={ButtonTheme.NORMAL}
                  onClick={() => retryMint(issuance, password, useWebhook, webhookUrl)}
                  style={{ width: 'fit-content', height: 'fit-content', padding: '5px 8px' }}
                >
                  <img src={retryIcon} alt="retry" style={{ width: 14, height: 14, objectFit: 'contain', marginRight: 10 }} />
                  Retry
                </BasicButton>
              )}
            </div>
            {issuance.tx_hash && (
              <a className="method-result-text" href={`${blockExplorerURL}/${issuance.tx_hash}`} target={'_blank'} rel="noreferrer">
                TX Hash : {JSON.stringify(issuance.tx_hash)}
              </a>
            )}
          </div>
        );
      })}
      <div className="method-result-text" style={{ fontSize: 14 }}>
        Check the transaction status on the block explorer
      </div>
    </div>
  );
};
